import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import AnswerReviewModal from '../../../components/AnswerReviewModal/AnswerReviewModal';
import StageHud from '../../../components/StageHud/StageHud';
import {
  GAME_COPY,
  GAME_KEYS,
  STAGE2_ANSWER_LABELS,
  STAGE2_LEFT,
  STAGE2_RIGHT,
} from '../../../data/games';
import { finishGame } from '../../../utils/gameSave';
import { useCarnivalTimer } from '../../../hooks/useCarnivalTimer';
import { useStagePoints } from '../../../hooks/useStagePoints';
import './AvoidConsiderPage.css';

function loadLeaderLine() {
  return new Promise((resolve, reject) => {
    if (window.LeaderLine) {
      resolve(window.LeaderLine);
      return;
    }
    const existing = document.querySelector('script[data-leader-line]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.LeaderLine));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = '/js/leader-line.min.js';
    script.async = true;
    script.dataset.leaderLine = '1';
    script.onload = () => resolve(window.LeaderLine);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

export default function AvoidConsiderPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const copy = GAME_COPY.stage2;

  const [selectedLeft, setSelectedLeft] = useState(null);
  const [usedLeft, setUsedLeft] = useState(() => new Set());
  const [usedRight, setUsedRight] = useState(() => new Set());
  const [rightColors, setRightColors] = useState(() => ({}));
  const [score, setScore] = useState(0);
  const [matchCount, setMatchCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const linesRef = useRef([]);
  const doneRef = useRef(false);
  const LeaderLineRef = useRef(null);

  const allMatched = matchCount >= STAGE2_LEFT.length;
  const { displayPoints, alreadyPlayed, markPlayedAndSyncCoins } = useStagePoints(GAME_KEYS.stage2, score);
  const { display: timerDisplay, elapsedRef } = useCarnivalTimer({ running: !alreadyPlayed });

  useEffect(() => {
    let cancelled = false;
    loadLeaderLine()
      .then((LL) => {
        if (!cancelled) LeaderLineRef.current = LL;
      })
      .catch((err) => console.error('LeaderLine failed to load', err));
    return () => {
      cancelled = true;
      linesRef.current.forEach((line) => {
        try {
          line.remove();
        } catch {
          /* ignore */
        }
      });
      linesRef.current = [];
    };
  }, []);

  useEffect(() => {
    const reposition = () => {
      linesRef.current.forEach((line) => {
        try {
          line.position();
        } catch {
          /* ignore */
        }
      });
    };
    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, []);

  /** Mirrors PHP makeNewLine(): hidden line, then animated "draw" reveal. */
  const drawLine = (beginId, endId, color) => {
    const LL = LeaderLineRef.current;
    if (!LL) return;
    const startEl = document.getElementById(beginId);
    const endEl = document.getElementById(endId);
    if (!startEl || !endEl) return;

    const line = new LL(startEl, endEl, { hide: true });
    line.color = color;
    line.show('draw', { duration: 1000, timing: 'linear' });
    linesRef.current.push(line);
  };

  const onLeftClick = (item) => {
    if (usedLeft.has(item.id) || busy) return;
    setSelectedLeft(item);
  };

  const onRightClick = (item) => {
    if (!selectedLeft) {
      Swal.fire({
        title: 'please select left column first',
        icon: 'info',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'hf-swal-close', popup: 'hf-swal-popup' },
        buttonsStyling: false,
      });
      return;
    }
    if (usedRight.has(item.id) || busy) return;

    const left = selectedLeft;
    const correct = left.pos === item.pos;
    if (correct) setScore((s) => s + 1);

    setRightColors((prev) => ({ ...prev, [item.id]: left.color }));

    drawLine(left.id, item.id, left.color);

    setUsedLeft((prev) => new Set(prev).add(left.id));
    setUsedRight((prev) => new Set(prev).add(item.id));
    setSelectedLeft(null);
    setMatchCount((c) => c + 1);
  };

  const openReview = async () => {
    if (!allMatched) {
      await Swal.fire({
        title: 'Please complete all matches',
        icon: 'warning',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'hf-swal-close', popup: 'hf-swal-popup' },
        buttonsStyling: false,
      });
      return;
    }
    setReviewOpen(true);
  };

  const submit = async () => {
    if (doneRef.current || busy || !user) return;
    doneRef.current = true;
    setBusy(true);
    setReviewOpen(false);
    try {
      const result = await finishGame({
        user,
        gameKey: GAME_KEYS.stage2,
        score,
        elapsedSeconds: elapsedRef.current,
      });
      markPlayedAndSyncCoins(result);
      if (result.alreadyPlayed) navigate('/hub');
      else navigate(`/thank-you?game=${GAME_KEYS.stage2}&points=${result.coinsEarned ?? 0}`);
    } catch (err) {
      console.error(err);
      doneRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div
      className="avoid-page stage-bg"
      style={{
        '--ic-bg': 'url(/img/BG_2.jpg)',
        '--ic-art': 'url(/stage2/img/stage2bg.jpg)',
        '--ic-art-mob': 'url(/stage2/img/stage2bg_mob.png)',
      }}
    >
      <div className="avoid-stage-art" aria-hidden="true" />
      <div className="avoid-alldata">
        <StageHud
          title={copy.title}
          instruction={copy.instruction}
          timerDisplay={timerDisplay}
          points={displayPoints}
          rules={copy.rules}
        />

        <div className="avoid-columns">
          <div className="avoid-col" id="left-column">
            <h4 id="leftdata">Term to Reconsider</h4>
            {STAGE2_LEFT.map((item) => (
              <button
                key={item.id}
                id={item.id}
                type="button"
                className={`column-left ${selectedLeft?.id === item.id ? 'selected' : ''} ${
                  usedLeft.has(item.id) ? 'used' : ''
                }`}
                style={{ backgroundColor: item.color }}
                disabled={usedLeft.has(item.id)}
                onClick={() => onLeftClick(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="avoid-col" id="right-column">
            <h4 id="rightdata">Alternative term to User</h4>
            {STAGE2_RIGHT.map((item) => (
              <button
                key={item.id}
                id={item.id}
                type="button"
                className={`column-right ${usedRight.has(item.id) ? 'used' : ''}`}
                style={
                  rightColors[item.id]
                    ? { backgroundColor: rightColors[item.id], color: '#fff' }
                    : undefined
                }
                disabled={usedRight.has(item.id)}
                onClick={() => onRightClick(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="avoid-actions">
          <button
            type="button"
            className="button-login1"
            disabled={busy || !allMatched}
            onClick={openReview}
          >
            Submit
          </button>
          <button type="button" className="resetbutton" onClick={() => window.location.reload()}>
            Reset
          </button>
        </div>
      </div>

      <div className="stage-characters" aria-hidden="true">
        <img className="stage-char-left" src="/stage2/img/left.png" alt="" />
        <img className="stage-char-right" src="/stage2/img/right.png" alt="" />
      </div>

      {reviewOpen && (
        <AnswerReviewModal
          title="Correct answers"
          lines={STAGE2_ANSWER_LABELS}
          onConfirm={submit}
        />
      )}
    </div>
  );
}
